import { useTranslation } from 'react-i18next';

import FormField from '@/components/atoms/FormField';
import Select from '@/components/atoms/Select';
import TextField from '@/components/atoms/TextField';

import type { FeedingConfigPen } from './FeedingConfigPanel';
import { C } from './feedingConfigFields';
import type { Scope } from './useFeedingConfigForm';

import styles from './FeedingConfigPanel.module.scss';

interface Props {
    scope: Scope;
    setScope: (scope: Scope) => void;
    canScopeToPen: boolean;
    pens: FeedingConfigPen[];
    activePen: FeedingConfigPen | undefined;
    systemName: string;
    setSystemName: (name: string) => void;
}

/** The site's planning-system label and the site-or-pen scope picker. */
export function FeedingConfigScopeFields(props: Props) {
    const { scope, setScope, canScopeToPen, pens, activePen, systemName, setSystemName } = props;
    const { t } = useTranslation();

    return (
        <>
            {/* Pens on one site sit at different points in the cycle, so
                    targets are per pen. Site values remain the default every
                    pen inherits, which keeps the common case one edit. */}
            {/* Site-only: the label names the customer's system, which is a
                    property of the site rather than of a pen. Free text, since
                    a rebrand or a system we have not met yet should not need a
                    deploy (IPS req 28). */}
            {scope === 'site' ? (
                <FormField
                    size="sm"
                    htmlFor="feeding-config-system"
                    className={styles.scope}
                    label={t(`${C}.fields.pmsSystemName`)}
                    hint={t(`${C}.pmsSystemNameHint`)}
                    hintTone="secondary">
                    <TextField
                        id="feeding-config-system"
                        type="text"
                        maxLength={40}
                        placeholder={t('feedingModule.pms')}
                        value={systemName}
                        onChange={(event) => setSystemName(event.target.value)}
                    />
                </FormField>
            ) : null}

            {canScopeToPen ? (
                <FormField
                    size="sm"
                    htmlFor="feeding-config-scope"
                    className={styles.scope}
                    label={t(`${C}.scope.label`)}
                    hint={t(activePen ? `${C}.scope.penHint` : `${C}.scope.siteHint`)}
                    hintTone="secondary">
                    <Select
                        id="feeding-config-scope"
                        value={scope === 'site' ? 'site' : String(scope)}
                        onChange={(event) =>
                            setScope(
                                event.target.value === 'site' ? 'site' : Number(event.target.value)
                            )
                        }>
                        <option value="site">{t(`${C}.scope.site`)}</option>
                        {pens.map((pen) => (
                            <option key={pen.cageId} value={String(pen.cageId)}>
                                {pen.cageName}
                            </option>
                        ))}
                    </Select>
                </FormField>
            ) : null}
        </>
    );
}
